import { BetSelection, BetType } from "@/types";

/** Named systems Betaxis takes, with their selection count and combination sizes. */
export const SYSTEM_TYPES: { key: string; selections: number; sizes: number[] }[] = [
  { key: "trixie", selections: 3, sizes: [2, 3] },
  { key: "patent", selections: 3, sizes: [1, 2, 3] },
  { key: "yankee", selections: 4, sizes: [2, 3, 4] },
  { key: "lucky15", selections: 4, sizes: [1, 2, 3, 4] },
  { key: "canadian", selections: 5, sizes: [2, 3, 4, 5] },
  { key: "lucky31", selections: 5, sizes: [1, 2, 3, 4, 5] },
  { key: "heinz", selections: 6, sizes: [2, 3, 4, 5, 6] },
  { key: "lucky63", selections: 6, sizes: [1, 2, 3, 4, 5, 6] },
  { key: "super_heinz", selections: 7, sizes: [2, 3, 4, 5, 6, 7] },
  { key: "goliath", selections: 8, sizes: [2, 3, 4, 5, 6, 7, 8] },
];

/** At most this many selections in a system slip (Betaxis BETS_MAX_SYSTEM_SELECTIONS). */
export const MAX_SYSTEM_SELECTIONS = 10;

const combinations = <T>(items: T[], size: number): T[][] => {
  if (size === 0) return [[]];
  return items.flatMap((item, index) =>
    combinations(items.slice(index + 1), size - 1).map((rest) => [item, ...rest]),
  );
};

/** Combination sizes of a system type over `count` selections, or null when it doesn't fit. */
export const systemSizes = (systemType: string, count: number): number[] | null => {
  const named = SYSTEM_TYPES.find((type) => type.key === systemType);
  if (named) return named.selections === count ? named.sizes : null;
  const match = /^(\d{1,2})\/(\d{1,2})$/.exec(systemType);
  if (!match) return null;
  const size = Number(match[1]);
  const total = Number(match[2]);
  return total === count && size >= 1 && size < total ? [size] : null;
};

/** Every system type that fits `count` selections: the named ones first, then k/n. */
export const systemTypesFor = (count: number): string[] => {
  if (count < 3 || count > MAX_SYSTEM_SELECTIONS) return [];
  const named = SYSTEM_TYPES.filter((type) => type.selections === count).map((type) => type.key);
  const kOfN = Array.from({ length: count - 1 }, (_, index) => `${index + 1}/${count}`).filter(
    (key) => key !== `1/${count}`,
  );
  return [...named, ...kOfN];
};

/** How many bets a system slip makes. */
export const systemBetCount = (systemType: string, count: number): number =>
  (systemSizes(systemType, count) ?? []).reduce(
    (sum, size) =>
      sum +
      combinations(
        Array.from({ length: count }, (_, i) => i),
        size,
      ).length,
    0,
  );

/** Payout if every bet of the system wins: the sum over its combinations. */
export const systemPayout = (systemType: string, bets: BetSelection[], unit: number): number =>
  (systemSizes(systemType, bets.length) ?? []).reduce(
    (sum, size) =>
      sum +
      combinations(bets, size).reduce(
        (comboSum, combo) =>
          comboSum + unit * combo.reduce((product, bet) => product * Number(bet.odds), 1),
        0,
      ),
    0,
  );

const distinctEvents = (bets: BetSelection[]) => new Set(bets.map((bet) => bet.eventId)).size;

export interface ModeLimits {
  /** Betaxis has the bet builder switched on (`BETS_BUILDER_ENABLED`); off by default. */
  builderEnabled?: boolean;
  maxSystemSelections?: number;
}

/**
 * The modes a set of picks allows: singles always; a multi or a system needs picks on
 * different matches; a builder needs 2+ picks on one match and the sportsbook to take
 * builders at all (it refuses them when off, after the stake was already debited).
 */
export const allowedModes = (bets: BetSelection[], limits: ModeLimits = {}): BetType[] => {
  const modes: BetType[] = ["single"];
  const sameEvent = bets.length >= 2 && distinctEvents(bets) === 1;
  const allDifferent = distinctEvents(bets) === bets.length;
  const maxSystem = limits.maxSystemSelections ?? MAX_SYSTEM_SELECTIONS;
  if (bets.length >= 2 && allDifferent) modes.push("multi");
  if (bets.length >= 3 && bets.length <= maxSystem && allDifferent) modes.push("system");
  if (sameEvent && limits.builderEnabled) modes.push("builder");
  return modes;
};
